import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './s.module.less';
import {
    EMessageSenderType,
    EMessageType,
    EWSAction,
    TServerMessage,
    TWSMessage,
    EConversationType,
    TGPScope,
    TConversation,
    TGPRecipientPatient,
} from 'types/inbox';
import { ClockCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { getTimeStr } from 'utils/calendar';
import dayjs from 'dayjs';
import InboxMessageList from './components/InboxMessageList';
import IntakeFormReminderButton from './components/IntakeFormReminderButton';
import AppointmentReminderButton from './components/AppointmentReminderButton';
import { EAIEditorType } from 'types/ai';
import useMyAllProspectList from 'hooks/useMyAllProspectList';
import ProsepectInfo from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo';
import { handleServerSideMessage, unitAndSortServerMessages, getInfoFromConversation } from 'utils/inbox';
import EmptyIcon from './imgs/empty.svg';
import { Button, Input, message, Select, Spin, Tooltip } from 'antd';
import { Prospect } from 'types/common';
import { getConversationMessageList, TGetConversationMessageList, markReadForConversation } from 'api/inbox';
import {
    gpSendMessage,
    gpGetMessages,
    gpMarkRead,
    gpSearchPatientRecipient,
    gpSearchTeamMemberRecipient,
    getConversationsListWithIdentity,
    getUnreadAmountWithIdentity,
} from 'api/group';
import { getUserId } from 'utils/localstore';
import useInboxConversations from 'hooks/inbox/useInboxConversations';
import useInboxAllUnreadCount from 'hooks/inbox/useInboxAllUnreadCount';
import useGPConversations from 'hooks/inbox/useGPConversations';
import useGPUnreadCount from 'hooks/inbox/useGPUnreadCount';
import { TWebSocketListener, useWebSocket } from 'hooks/inbox/useWebSocket';
import { VALID_DOMAIN } from 'constants/common';
import { canShowGroupPracticeView, getUrlParam } from 'utils/common';
import useProspectInfo from 'hooks/useProspectInfo';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { useGetConnectAccountInfo } from 'hooks/useGetConnectAccountInfo';
import { useBillingPolicy } from 'hooks/paymentSetup/useBillingPolicy';
import ChatInput, { ChatInputValue } from 'components/ChatInput';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import { CaseListItem } from 'types/textVisit';
import useAllTeamMember from 'hooks/group/useAllTeamMember';
import useCurrentUser from 'hooks/useCurrentUser';
import { ERoleCode } from 'types/enumItems';
import { TTeamMemberDto } from 'types/group';

type Props = {
    clickSendMsgFlag?: {};
    showGroupPracticeView?: boolean;
    gpViewAsProviderId?: number;
};

type SendToType = 'patient' | 'team';

// 会话列表最后一条预览：保险卡两类卡片的 content 是 payload JSON，映射成短语；其余原样
const conversationPreview = (item: TConversation): string => {
    if (item.lastMessageType === EMessageType.INSURANCE_CARD_REQUEST) return 'Requested an updated insurance card';
    if (item.lastMessageType === EMessageType.INSURANCE_CARD_UPLOADED) return 'Patient uploaded an insurance card';
    return item.lastMessageContent;
};

const DEFAULT_FETCH_MESSAGE_COUNT = 30;
const DEFAULT_FETCH_CONVERSATION_COUNT = 200;
const LOAD_HISTORY_INTERVAL = 1000 * 2;

type GPConvType = 'GP_STAFF_PATIENT' | 'GP_INTERNAL' | 'NORMAL';
type GPConvParsed = {
    type: GPConvType;
    identityId?: number;
    userIdA?: number;
    userIdB?: number;
};

const parseGPConversationId = (conversationId: string): GPConvParsed => {
    if (conversationId.startsWith('GP_PT_')) {
        const parts = conversationId.split('_');
        return { type: 'GP_STAFF_PATIENT', identityId: Number(parts[4]) };
    }
    if (conversationId.startsWith('GP_IN_')) {
        const parts = conversationId.split('_');
        return { type: 'GP_INTERNAL', userIdA: Number(parts[3]), userIdB: Number(parts[4]) };
    }
    return { type: 'NORMAL' };
};

const GP_ROLE_BADGE_STYLES: Record<string, { bg: string; color: string; label: string }> = {
    [ERoleCode.GROUP_PROVIDER]: { bg: '#EDE9FE', color: '#5B21B6', label: 'Provider' },
    [ERoleCode.GROUP_PRACTICE_MANAGER]: { bg: '#FEF3C7', color: '#92400E', label: 'Practice manager' },
    [ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT]: { bg: '#DBEAFE', color: '#1E40AF', label: 'Admin assistant' },
    [ERoleCode.GROUP_CLINICAL_ASSISTANT]: { bg: '#D1FAE5', color: '#065F46', label: 'Clinical assistant (MA)' },
    [ERoleCode.GROUP_OWNER]: { bg: '#F3F4F6', color: '#4B5563', label: 'Staff' },
};

const AVATAR_COLORS = ['#0E62F4', '#059669', '#D97706', '#1E40AF', '#065F46', '#92400E'];

const getAvatarColor = (name: string): string => {
    const code = name.charCodeAt(0) || 0;
    return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

type GPDisplayInfo = {
    displayTitle: string;
    labelText: string;
    roleBadge: React.ReactNode;
    avatarColor: string;
};

const getInitials = (firstName?: string, lastName?: string): string =>
    `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();

const getGPConversationDisplayInfo = (
    gpParsed: GPConvParsed,
    gpPatientResults: TGPRecipientPatient[],
    teamMembers: TTeamMemberDto[] | undefined,
    myUserId: number | undefined,
    myActingUserId?: number | undefined,
): GPDisplayInfo => {
    const result: GPDisplayInfo = {
        displayTitle: '',
        labelText: '',
        roleBadge: null,
        avatarColor: AVATAR_COLORS[0],
    };

    if (gpParsed.type === 'GP_STAFF_PATIENT') {
        const patient = gpPatientResults.find((p) => p.identityId === gpParsed.identityId);
        result.displayTitle = patient ? `${patient.firstName} ${patient.lastName}` : '';
        result.labelText = getInitials(patient?.firstName, patient?.lastName);
        result.avatarColor = getAvatarColor(patient?.firstName || '');
        result.roleBadge = <span className={`${s.roleBadge} ${s.roleBadgePatient}`}>Patient</span>;
    } else if (gpParsed.type === 'GP_INTERNAL') {
        const isUserA = gpParsed.userIdA === myUserId || gpParsed.userIdA === myActingUserId;
        const otherUserId = isUserA ? gpParsed.userIdB : gpParsed.userIdA;
        const member = teamMembers?.find((tm) => tm.userId === otherUserId);
        result.displayTitle = member ? `${member.firstName} ${member.lastName}` : '';
        result.labelText = getInitials(member?.firstName, member?.lastName);
        result.avatarColor = getAvatarColor(member?.firstName || '');
        if (member?.roleCode) {
            const badgeStyle = GP_ROLE_BADGE_STYLES[member.roleCode];
            if (badgeStyle) {
                result.roleBadge = (
                    <span className={s.roleBadge} style={{ background: badgeStyle.bg, color: badgeStyle.color }}>
                        {badgeStyle.label}
                    </span>
                );
            } else {
                result.roleBadge = <span className={`${s.roleBadge} ${s.roleBadgeStaff}`}>Staff</span>;
            }
        }
    }
    return result;
};

const MessageContainer = (props: Props) => {
    const { clickSendMsgFlag, showGroupPracticeView, gpViewAsProviderId } = props;
    const navigate = useNavigate();
    const searchParams = () => new URLSearchParams(window.location.search);
    const client_id = searchParams().get('client_id');
    // const [get, set] = InboxStore.useStore();

    const conversationMessageMap = React.useRef<Record<string, TServerMessage[] | undefined>>({});
    const allConversationMessageFetchedFlag = React.useRef<Record<string, boolean>>({});
    const latestConversationMessageCursor = React.useRef<Record<string, string>>({});
    const latestLoadHistoryTime = React.useRef<number>(new Date().getTime());
    const [hasFocusOnDefaultConversation, setHasFocusOnDefaultConversation] = useState<boolean>(false);

    const [selectConversationId, setSelectConversationId] = useState<string>();
    const selectConversationIdRef = React.useRef<string | undefined>(selectConversationId);

    const fetchConversationSize = useRef<number>(DEFAULT_FETCH_CONVERSATION_COUNT);

    const urlClientId = useRef<string | undefined>(getUrlParam('clientId') || undefined);

    const [conversation, , refetchConversation] = useInboxConversations(
        fetchConversationSize.current,
        urlClientId.current,
    );
    const [allUnreadCount, unreadLoading, refetchAllUnreadCount] = useInboxAllUnreadCount();

    const [showUnreadConversationOnly, setShowUnreadConversationOnly] = useState<boolean>(false);
    const [loadingConversationMessage, setLoadingConversationMessage] = useState<boolean>(false);

    const [showClientInfo, setShowClientInfo] = useState<boolean>(false);
    const loadingHistoryMessageRef = useRef<boolean>(false);
    const [, forceUpdate] = useState<{}>();

    const [messageListUnreadCount, setMessageListUnreadCount] = useState<number>(0);

    const autoFetchConversationInterval = React.useRef<NodeJS.Timer>();

    const hasMoreConversation = !!(conversation && conversation.length === fetchConversationSize.current);
    const [forceResetInfo, setForceResetInfo] = useState<{}>({});

    const providerId = getUserId();

    const [homeInfo, loadingHomeInfo, refetchHomeInfo] = useProviderHomeInfo();
    const isPPAProvider = homeInfo?.ppaProvider;

    const [connectAccountInfo, loadingConnectAccountInfo] = useGetConnectAccountInfo();
    const [billingPolicy, loadingBillingPolicy] = useBillingPolicy(false, providerId);

    const [showTextVisitUnSubmittedDrawer, setShowTextVisitUnSubmittedDrawer] = useState(false);
    const [selectedCase, setSelectedCase] = useState<CaseListItem>();
    const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);
    const [nextCaseUuid, setNextCaseUuid] = useState<string>();

    const scrollToLatestMessage = (timeout?: number, smooth?: boolean) => {
        setTimeout(() => {
            const messageWrap = document.getElementById('messageWrap');
            if (messageWrap) {
                messageWrap.scrollTo({
                    top: 99999,
                    behavior: smooth ? 'smooth' : 'auto',
                });
            }
        }, timeout || 150);
    };

    const conversationId = selectConversationId || selectConversationIdRef.current;
    let isProviderChat = false;
    let patientId: number | undefined;
    if (conversationId) {
        const gpParsed = parseGPConversationId(conversationId);
        if (gpParsed.type === 'GP_STAFF_PATIENT') {
            patientId = gpParsed.identityId;
        } else if (gpParsed.type === 'GP_INTERNAL') {
            isProviderChat = true;
        } else {
            const parts = conversationId.split('_');
            if (parts.length === 2) {
                patientId = parseInt(parts[1], 10);
            }
        }
    }

    const isGPStaffPatient = conversationId ? parseGPConversationId(conversationId).type === 'GP_STAFF_PATIENT' : false;
    const [targetClientDetail, loadingTargetClientDetail, runTargetClientDetail] = useProspectInfo(
        patientId,
        showGroupPracticeView && isGPStaffPatient,
    );

    const resetAutoFetchInterval = () => {
        if (autoFetchConversationInterval.current) {
            clearInterval(autoFetchConversationInterval.current);
        }
        autoFetchConversationInterval.current = setInterval(() => {
            refetchConversation(fetchConversationSize.current, urlClientId.current);
            refetchAllUnreadCount();
        }, 1000 * 8); // 5 s
    };
    const [messageList, setMessageList] = useState<TServerMessage[]>();
    const [replayContent, setReplayContent] = useState<string>('');
    const [replayContentValue, setReplayContentValue] = useState<ChatInputValue>({ text: replayContent, files: [] });
    const [resetFlag, setResetFlag] = useState<{}>({});
    const [clearMessageFlag, setClearMessageFlag] = useState<{}>();
    const [allClient, loading, run] = useMyAllProspectList();

    const [newMsgClientId, setNewMsgClientId] = useState<number>();
    const [newMsgContent, setNewMsgContent] = useState<ChatInputValue>({ text: '', files: [] });
    const [showNewMsgModal, setShowNewMsgModal] = useState<boolean>(false);
    const [sendingNewMsg, setSendingNewMsg] = useState<boolean>(false);
    const [sendToType, setSendToType] = useState<SendToType>('patient');
    const [teamMembers] = useAllTeamMember();
    const [currentUser] = useCurrentUser();
    //New message 逻辑
    //isGroupOwner， my时: 2个tab，，patient用clientOption，team用gpTeamMemberResults, all时:2个tab， patient用gpPatientResults,team用gpTeamMemberResults
    //isStaff, my时：2个tab， patient用gpPatientResults,team用gpTeamMemberResults, all时:2个tab， patient用gpPatientResults,team用gpTeamMemberResults
    //isProviderWithoutGroup,没有tab，patient用clientOption
    //isProviderWithGroup, 2个tab，patient用clientOption，team用gpTeamMemberResults
    const isGroupOwner = currentUser && currentUser.roleCode === ERoleCode.GROUP_OWNER && currentUser.groupPractice;
    const isStaff =
        currentUser &&
        [
            ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT,
            ERoleCode.GROUP_CLINICAL_ASSISTANT,
            ERoleCode.GROUP_PRACTICE_MANAGER,
        ].includes(currentUser.roleCode as ERoleCode);
    const isProviderWithoutGroup =
        currentUser && currentUser.roleCode === ERoleCode.GROUP_OWNER && !currentUser.groupPractice;
    const isProviderWithGroup =
        currentUser && currentUser.roleCode === ERoleCode.GROUP_PROVIDER && currentUser.groupPractice;
    const [gpPatientResults, setGPPatientResults] = useState<TGPRecipientPatient[]>([]);
    const isInGroup = !!currentUser?.groupPractice;

    const [gpTeamMemberResults, setGPTeamMemberResults] = useState<TTeamMemberDto[]>([]);

    React.useEffect(() => {
        if (showGroupPracticeView || isInGroup) {
            gpSearchPatientRecipient('')
                .then((res) => {
                    if (!res.error) {
                        const patients: TGPRecipientPatient[] = res?.data?.data || [];
                        setGPPatientResults(patients.filter((p) => !p.email?.endsWith('@example.com')));
                    }
                })
                .catch(console.error);
            gpSearchTeamMemberRecipient('')
                .then((res) => {
                    if (!res.error) {
                        setGPTeamMemberResults(res?.data?.data || []);
                    }
                })
                .catch(console.error);
        }
    }, [showGroupPracticeView, isInGroup]);

    // GP state
    const [gpScope, setGPScope] = useState<TGPScope>('my');
    const showBottomAction = isPPAProvider && (!showGroupPracticeView || (showGroupPracticeView && gpScope === 'my'));

    // New message: role-based tab & patient selector logic
    // isGroupOwner       → my: 2 tabs, clientOption       | all: 2 tabs, gpPatientResults
    // isStaff            → my: 2 tabs, gpPatientResults   | all: 2 tabs, gpPatientResults
    // isProviderWithGroup→ 2 tabs, clientOption
    // isProviderWithoutGroup → no tab, clientOption
    const showTeamTab = !!(isGroupOwner || isStaff || isProviderWithGroup);
    const useGPPatientSearch = !!(isStaff || (isGroupOwner && gpScope === 'all'));

    const [gpSelectedProviderId, setGPSelectedProviderId] = useState<number>();
    const [gpSearchQuery, setGPSearchQuery] = useState<string>('');
    const [gpConversations, , refetchGPConversations] = useGPConversations(
        DEFAULT_FETCH_CONVERSATION_COUNT,
        showGroupPracticeView ? gpScope : undefined,
    );
    const [gpUnreadCount, , refetchGPUnreadCount] = useGPUnreadCount();

    // Tab2 (All messages) state for provider-view conversations
    //'/schedule-server/provider/conversations'
    const [allMsgConversations, setAllMsgConversations] = useState<TConversation[]>();
    const [, setAllMsgLoading] = useState(false);
    const [allMsgUnreadCount, setAllMsgUnreadCount] = useState<number>();
    const isActingMyself = currentUser?.actingMode === 'SELF';

    const disabledSendToPatient = !isActingMyself && currentUser?.roleCode === ERoleCode.GROUP_OWNER;

    const showPatientTab = !disabledSendToPatient;
    const showSendToToggle = showPatientTab && showTeamTab;

    const providerMembers = React.useMemo(
        () => teamMembers?.filter((m) => m.roleCode === ERoleCode.GROUP_PROVIDER) || [],
        [teamMembers],
    );

    const gpSelectedProvider = providerMembers.find((p) => p.providerId === gpSelectedProviderId);

    const fetchAllMsgConversations = async (identityId: number) => {
        try {
            setAllMsgLoading(true);
            const result = await getConversationsListWithIdentity(1, DEFAULT_FETCH_CONVERSATION_COUNT, identityId);
            if (!result.error) {
                setAllMsgConversations(result?.data?.data?.records || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setAllMsgLoading(false);
        }
    };

    const fetchAllMsgUnreadCount = async (identityId: number) => {
        try {
            const result = await getUnreadAmountWithIdentity(identityId);
            if (!result.error) {
                setAllMsgUnreadCount(result?.data?.data || 0);
            }
        } catch (e) {
            console.error(e);
        }
    };

    // Determine active conversation list based on GP scope
    const activeConversations = React.useMemo(() => {
        if (!showGroupPracticeView) return conversation;
        return gpConversations;
    }, [showGroupPracticeView, conversation, gpConversations]);

    const effectiveConversations = showGroupPracticeView ? activeConversations : conversation;
    const targetConversation = effectiveConversations?.find((item) => item.conversationId === selectConversationId);
    const targetConversationInfo = getInfoFromConversation(targetConversation);

    const activeUnreadCount = React.useMemo(() => {
        if (!showGroupPracticeView) return allUnreadCount;
        return gpUnreadCount;
    }, [showGroupPracticeView, allUnreadCount, gpUnreadCount]);

    const [replyingMsg, setReplyingMsg] = useState<boolean>(false);

    const _targetClient = allClient.find((item) => item.id === patientId);

    const targetClient = targetClientDetail || _targetClient;

    const isTargetProviderReview =
        targetConversation &&
        (targetConversation.conversationType === EConversationType.PROVIDER_REVIEW ||
            targetConversation.conversationType === 'PROVIDER_REVIEW');

    const showInput = !showGroupPracticeView || (gpScope === 'all' && isActingMyself) || gpScope === 'my';

    let conversationLabelText = '';
    let conversationDisplayTitle = '';
    let conversationRoleBadge: React.ReactNode = null;
    let conversationAvatarColor = AVATAR_COLORS[0];
    if (isTargetProviderReview) {
        conversationLabelText = 'KR';
        conversationDisplayTitle = 'Klarity reviews';
    } else if (conversationId && (showGroupPracticeView || isInGroup)) {
        const gpParsed = parseGPConversationId(conversationId);
        if (gpParsed.type !== 'NORMAL') {
            const myUserId = currentUser?.user?.id;
            const myActingUserId = currentUser?.actingUser?.id;
            const gpInfo = getGPConversationDisplayInfo(
                gpParsed,
                gpPatientResults,
                teamMembers,
                myUserId,
                myActingUserId,
            );
            conversationLabelText = gpInfo.labelText;
            conversationDisplayTitle = gpInfo.displayTitle;
            conversationRoleBadge = gpInfo.roleBadge;
            conversationAvatarColor = gpInfo.avatarColor;
        }
    }
    if (!conversationDisplayTitle && !isTargetProviderReview) {
        conversationDisplayTitle =
            `${targetConversationInfo?.patientFirstName || ''} ${targetConversationInfo?.patientLastName || ''}`.trim();
    }
    if (!conversationLabelText) {
        conversationLabelText = getInitials(
            targetConversationInfo?.patientFirstName,
            targetConversationInfo?.patientLastName,
        );
        conversationAvatarColor = getAvatarColor(targetConversationInfo?.patientFirstName || '');
    }
    const isEmpty = !effectiveConversations || effectiveConversations?.length === 0;
    const clientOption = allClient?.filter((item) => !item.isSample);
    const isOwnerMySelf = isActingMyself && currentUser?.roleCode === ERoleCode.GROUP_OWNER;
    const currentInConversation = !!targetConversation?.members?.find((m) => m.userId === currentUser?.user?.id);
    const disabledSendAndAddHolder = isOwnerMySelf && !currentInConversation && gpScope === 'all';

    const hideUnread = showGroupPracticeView && gpScope === 'all';
    // GP scope change effects
    React.useEffect(() => {
        if (!showGroupPracticeView) return;
        setSelectConversationId(undefined);
        selectConversationIdRef.current = undefined;
        setMessageList(undefined);
        setGPSearchQuery('');
    }, [gpScope]);

    React.useEffect(() => {
        if (!showGroupPracticeView) return;
        if (gpViewAsProviderId) {
            setGPScope('all');
            setGPSelectedProviderId(gpViewAsProviderId);
        } else if (gpScope === 'all') {
            setGPSelectedProviderId(undefined);
            setAllMsgConversations(undefined);
        }
    }, [gpViewAsProviderId]);

    React.useEffect(() => {
        if (!showGroupPracticeView || !urlClientId.current) return;
        setGPScope('all');
        setGPSelectedProviderId(Number(urlClientId.current));
    }, [showGroupPracticeView]);

    React.useEffect(() => {
        if (!showGroupPracticeView || gpScope !== 'all' || !gpSelectedProviderId) return;
        fetchAllMsgConversations(gpSelectedProviderId);
        fetchAllMsgUnreadCount(gpSelectedProviderId);
    }, [gpSelectedProviderId, gpScope]);

    const handleScopeChange = (scope: TGPScope) => {
        setGPScope(scope);
        setShowUnreadConversationOnly(false);
    };

    const socketListeners = React.useRef<TWebSocketListener>({
        onOpen: (event) => {
            console.log('WebSocket connection opened:', event);
            // Optionally, you can send a message or perform an action when the connection opens
            // socketRef.current.send(JSON.stringify({ type: 'subscribe', channel: 'inbox' }));
            resetAutoFetchInterval();
            refetchConversation(fetchConversationSize.current, urlClientId.current);
            refetchAllUnreadCount();
        },
        onClose: (event) => {
            console.warn('WebSocket connection closed:', event);
            // Optionally, you can try to reconnect here
            // socketRef.current = new WebSocket(WEBSOCKET_URL);
        },
        onMessage: (event) => {
            console.info('WebSocket onMessage:', event);
            const msgData = event.data;
            try {
                const wsMsg: TWSMessage = JSON.parse(msgData);
                if (wsMsg.action === EWSAction.NEWMSG) {
                    const serverMsg = wsMsg.payload as TServerMessage;
                    const conversationIdFromWs = serverMsg.conversationId;
                    const cacheMessageList = conversationMessageMap.current[conversationIdFromWs] || [];
                    let _message = unitAndSortServerMessages([...(cacheMessageList || []), serverMsg]);

                    const currentSortId = serverMsg.sortKey;
                    const targetIndex = cacheMessageList.findIndex((item) => item.sortKey === currentSortId);
                    if (targetIndex >= 0) {
                        cacheMessageList[targetIndex] = { ...serverMsg };
                        _message = unitAndSortServerMessages(cacheMessageList);
                    }

                    conversationMessageMap.current[conversationIdFromWs] = _message || [];
                    // console.log({
                    //     _message,
                    // })
                    if (selectConversationIdRef.current === conversationIdFromWs) {
                        setMessageList(_message);
                        scrollToLatestMessage(undefined, true);
                    }
                } else {
                    console.warn('Unknown WebSocket action:', msgData);
                }
            } catch (e) {
                console.error(e);
            }
        },
        onError: (event) => {
            console.error('WebSocket error:', event);
        },
        autoCloseTime: 1000 * 60 * 10, // 10 minutes
        autoReconnectTime: 1000 * 20, // 20 seconds
    });

    const [socketRef] = useWebSocket(socketListeners.current);

    const displayMessage = handleServerSideMessage(messageList, messageListUnreadCount);
    const displayConversation = effectiveConversations?.filter((item) => {
        if (item.conversationId === selectConversationId) {
            return true;
        }
        if (showUnreadConversationOnly) {
            return item.unreadCount && item.unreadCount > 0;
        }
        if (gpSearchQuery && showGroupPracticeView) {
            const query = gpSearchQuery.toLowerCase();
            const parsed = parseGPConversationId(item.conversationId);
            let convName = '';
            if (parsed.type === 'GP_STAFF_PATIENT') {
                const patient = gpPatientResults.find((p) => p.identityId === parsed.identityId);
                convName = patient ? `${patient.firstName} ${patient.lastName}` : '';
            } else if (parsed.type === 'GP_INTERNAL') {
                const myUserId = currentUser?.user?.id;
                const myActingUserId = currentUser?.actingUser?.id;
                const isUserA = parsed.userIdA === myUserId || parsed.userIdA === myActingUserId;
                const otherUserId = isUserA ? parsed.userIdB : parsed.userIdA;
                const member = teamMembers?.find((tm) => tm.userId === otherUserId);
                convName = member ? `${member.firstName} ${member.lastName}` : '';
            } else {
                const info = getInfoFromConversation(item);
                convName = `${info.patientFirstName || ''} ${info.patientLastName || ''}`;
            }
            return (
                convName.toLowerCase().includes(query) || (item.lastMessageContent || '').toLowerCase().includes(query)
            );
        }
        return true;
    });
    const effectiveUnreadCount = showGroupPracticeView ? activeUnreadCount : allUnreadCount;
    const hasUnreadMessageAll = !!(effectiveUnreadCount && effectiveUnreadCount > 0);

    React.useEffect(() => {
        if (patientId && showClientInfo) {
        }
    }, [patientId, showClientInfo]);

    React.useEffect(() => {
        if (showGroupPracticeView) return;
        if (!client_id || !conversation || selectConversationId) return;

        let initConversationId = '';
        if (client_id) {
            initConversationId = conversation.find((item) => item.clientId === Number(client_id))?.conversationId ?? '';
            if (initConversationId) {
                handleChangeConversation(initConversationId);
            } else {
                setShowNewMsgModal(true);
                setNewMsgClientId(Number(client_id));
                setNewMsgContent({ text: '', files: [] });
                setResetFlag({});
            }
        }
    }, [conversation, client_id]);

    React.useEffect(() => {
        if (hasFocusOnDefaultConversation || !urlClientId.current) {
            return;
        }
        if (showGroupPracticeView) {
            return;
        }
        const targetCid = `${getUserId()}_${urlClientId.current}`;
        const targetConversationFromUrl = conversation?.find((item) => item.conversationId === targetCid);
        if (targetConversationFromUrl) {
            setHasFocusOnDefaultConversation(true);
            setSelectConversationId(targetCid);
            selectConversationIdRef.current = targetCid;
        }
    }, [conversation, hasFocusOnDefaultConversation]);

    const handleShowMsgModal = () => {
        setShowNewMsgModal(true);
        setNewMsgClientId(undefined);
        setNewMsgContent({ text: '', files: [] });
        setResetFlag({});
        setSendToType(showPatientTab ? 'patient' : 'team');
    };

    const checkContent = (content: string): boolean => {
        const regex = /https?:\/\/[^\s，,；;\]\)\(\[\“\"\'<>]+/g;
        const links = content.match(regex);
        let isValid = true;
        links?.forEach((link) => {
            try {
                const url = new URL(link);
                if (url.hostname) {
                    let hostIsValid = false;
                    VALID_DOMAIN.forEach((domain) => {
                        if (url.hostname.endsWith(domain)) {
                            hostIsValid = true;
                        }
                    });
                    if (!hostIsValid) {
                        isValid = false;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        });
        return isValid;
    };

    const onClickSend = async () => {
        if (!newMsgClientId || (!newMsgContent.text.trim() && newMsgContent.files.length === 0) || !providerId) {
            message.error('Please select a client and enter a message.');
            return;
        }

        const msg = newMsgContent.text.trim() ? newMsgContent.text.replace(/<[^>]+>/g, '') : '';

        let recipientType: string;
        if (sendToType === 'team') {
            recipientType = 'GP_USER';
        } else if (useGPPatientSearch) {
            // 下拉 value 是 identityId（klarity_organization_prospect_identity.id）
            recipientType = 'GP_PROSPECT_IDENTITY';
        } else {
            // 下拉 value 是 prospect.id
            recipientType = 'PATIENT';
        }

        const doNewSend = async (msgText?: string, fileId?: number) => {
            return gpSendMessage({
                recipientType,
                targetId: newMsgClientId,
                message: msgText,
                fileId,
            });
        };

        setSendingNewMsg(true);

        try {
            if (msg) {
                const res = await doNewSend(msg);
                if (res?.error) {
                    // 错误 toast 已由 request.ts 弹过，这里只需中断，保留弹窗和已输入内容
                    return;
                }
            }
            if (newMsgContent.files.length > 0) {
                await Promise.all(
                    newMsgContent.files.map(async (file) => {
                        const resFile = await doNewSend(undefined, file.id);
                        if (!resFile.error) {
                            return resFile.data.data;
                        }
                        return null;
                    }),
                );
            }
            message.success('Sent successfully!');
            setShowNewMsgModal(false);
            setNewMsgClientId(undefined);
            setNewMsgContent({ text: '', files: [] });
            setResetFlag({});
            resetAutoFetchInterval();
            setTimeout(() => {
                if (showGroupPracticeView) {
                    refetchGPConversations(fetchConversationSize.current);
                    refetchGPUnreadCount();
                }
                refetchConversation(fetchConversationSize.current, urlClientId.current);
                refetchAllUnreadCount();
            }, 1000);
        } catch (error) {
            console.error('error:', error);
        } finally {
            setSendingNewMsg(false);
        }
    };

    const onClickReply = async () => {
        if (replyingMsg) {
            return;
        }
        if (!targetClientDetail?.id) {
            run();
        }
        if (!replayContentValue.text.trim() && replayContentValue.files.length === 0) {
            return;
        }
        const messageStr = replayContentValue.text.trim() ? replayContentValue.text.replace(/<[^>]+>/g, '') : '';
        const files = replayContentValue.files.map((file) => file.id);

        const doSend = async (msg?: string, fileId?: number) => {
            return gpSendMessage({
                conversationId: selectConversationId,
                message: msg,
                fileId,
            });
        };

        try {
            setReplyingMsg(true);
            if (messageStr) {
                const res = await doSend(messageStr);

                if (!res.error) {
                    setResetFlag({});
                    setClearMessageFlag({});

                    const messageData: TServerMessage = res.data?.data;
                    const cacheMessageList = conversationMessageMap.current[selectConversationId!] || [];
                    const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
                    conversationMessageMap.current[selectConversationId!] = _message || [];
                    setMessageList(_message || []);
                }
            }
            if (files.length > 0) {
                const res = await Promise.all(
                    replayContentValue.files.map(async (file) => {
                        const resFile = await doSend(undefined, file.id);
                        if (!resFile.error) {
                            return resFile.data.data;
                        }
                        return null;
                    }),
                );
                res.forEach((newMsg) => {
                    if (newMsg) {
                        const cacheMessageList = conversationMessageMap.current[selectConversationId!] || [];
                        const _message = unitAndSortServerMessages([...(cacheMessageList || []), newMsg]);
                        conversationMessageMap.current[selectConversationId!] = _message || [];
                        setMessageList(_message || []);
                    }
                });
            }
            setReplayContentValue({ text: '', files: [] });
            scrollToLatestMessage();
            resetAutoFetchInterval();
            setTimeout(() => {
                if (showGroupPracticeView) {
                    refetchGPConversations(fetchConversationSize.current);
                    refetchGPUnreadCount();
                } else {
                    refetchConversation(fetchConversationSize.current, urlClientId.current);
                    refetchAllUnreadCount();
                }
            }, 2000);
        } catch (error) {
            console.error('error:', error);
        } finally {
            setReplyingMsg(false);
        }
    };

    const onClickDiscard = async () => {
        setNewMsgClientId(undefined);
        setNewMsgContent({ text: '', files: [] });
        setResetFlag({});
    };

    const handleChangeConversation = async (cid: string) => {
        const cacheMessage = conversationMessageMap.current[cid] || [];
        setMessageList(cacheMessage);
        setSelectConversationId(cid);
        const _targetConversation = effectiveConversations?.find((item) => item.conversationId === cid);
        if (_targetConversation) {
            setMessageListUnreadCount(_targetConversation.unreadCount || 0);
        }

        const isGPMode = !!showGroupPracticeView;
        const markReadFn = isGPMode ? gpMarkRead : markReadForConversation;

        if (cid) {
            const isMyConversation = _targetConversation?.members?.some(
                (m) => m.userId === getUserId() || m.userId === currentUser?.user?.id,
            );
            if (isMyConversation) {
                const res = await markReadFn(cid);
                if (!res.error) {
                    resetAutoFetchInterval();
                    if (isGPMode) {
                        refetchGPConversations(fetchConversationSize.current);
                        refetchGPUnreadCount();
                    } else {
                        refetchConversation(fetchConversationSize.current, urlClientId.current);
                        refetchAllUnreadCount();
                    }
                }
            }
        }
    };

    const fetchMessageByConversationId = async (_cid: string, cursor?: string, size?: number) => {
        const isGPMode = !!showGroupPracticeView;
        const param: TGetConversationMessageList = {
            conversationId: _cid,
            size: size || DEFAULT_FETCH_MESSAGE_COUNT,
        };
        if (cursor) {
            param.cursor = cursor;
        }
        setLoadingConversationMessage(true);
        const res = isGPMode
            ? await gpGetMessages({ conversationId: _cid, size: size || DEFAULT_FETCH_MESSAGE_COUNT, cursor })
            : await getConversationMessageList(param);
        if (!res.error) {
            const data = res?.data?.data?.items || [];
            const _isEmpty = data.length === 0;
            const _cursor = res?.data?.data?.cursor;
            const currentCursor = latestConversationMessageCursor.current[_cid] || '';
            if (!currentCursor || cursor) {
                //mean loading history or loading new but with any cursor
                latestConversationMessageCursor.current[_cid] = _cursor || '';
            }

            if (_isEmpty || !_cursor) {
                allConversationMessageFetchedFlag.current[_cid] = true;
            }

            // 合并缓存和新获取的消息
            // unitAndSortServerMessages 已经优化为：如果遇到相同的 sortKey，会保留最新的消息（基于 updatedAt）
            // 这样可以确保新消息（包含更新的回复内容）会覆盖旧缓存中的消息
            const cacheMessageList = conversationMessageMap.current[_cid] || [];
            const _message = unitAndSortServerMessages([...(cacheMessageList || []), ...data]);
            conversationMessageMap.current[_cid] = _message || [];
            if (_message && _message[_message.length - 1] && _message[_message.length - 1].aiReplyContent) {
                //last message
                setReplayContent(_message[_message.length - 1].aiReplyContent!);
            }
            setMessageList(_message);
            if (!cursor) {
                scrollToLatestMessage();
            }
        }
        setLoadingConversationMessage(false);
    };

    const loadHistoryMessage = async () => {
        if (loadingHistoryMessageRef.current === true) {
            return;
        }
        const targetConversationId = selectConversationIdRef.current;
        if (!targetConversationId) {
            return;
        }
        const cursor = latestConversationMessageCursor.current[targetConversationId] || '';
        if (!cursor) {
            return;
        }

        const allLoad = allConversationMessageFetchedFlag.current[targetConversationId];
        if (allLoad) {
            return;
        }
        const nowTime = new Date().getTime();
        const latestLoadTime = latestLoadHistoryTime.current;
        const diff = nowTime - latestLoadTime;
        if (diff < LOAD_HISTORY_INTERVAL) {
            return;
        }
        latestLoadHistoryTime.current = nowTime;
        loadingHistoryMessageRef.current = true;
        forceUpdate({});
        await fetchMessageByConversationId(targetConversationId, cursor, DEFAULT_FETCH_MESSAGE_COUNT);
        loadingHistoryMessageRef.current = false;
        forceUpdate({});
    };
    React.useEffect(() => {
        if (isEmpty) {
            return;
        }
        const messageScrollCheck = (e: Event) => {
            const target = e.target as HTMLElement;
            const { scrollTop } = target;
            if (scrollTop === 0) {
                loadHistoryMessage();
            }
        };

        const messageWrap = document.getElementById('messageWrap');
        if (messageWrap) {
            messageWrap.addEventListener('scroll', messageScrollCheck);
        }
        return () => {
            if (messageWrap) {
                messageWrap.removeEventListener('scroll', messageScrollCheck);
            }
        };
    }, [isEmpty]);

    React.useEffect(() => {
        return () => {
            if (socketRef) {
                socketRef.close();
            }
        };
    }, []);

    React.useEffect(() => {
        resetAutoFetchInterval();
        return () => {
            if (autoFetchConversationInterval.current) {
                clearInterval(autoFetchConversationInterval.current);
            }
        };
    }, []);

    React.useEffect(() => {
        const list = effectiveConversations;
        if (list && list.length > 0) {
            const currentNotInList =
                selectConversationId && !list.find((item) => item.conversationId === selectConversationId);
            if ((!selectConversationId && !selectConversationIdRef.current) || currentNotInList) {
                setSelectConversationId(list[0].conversationId);
                selectConversationIdRef.current = list[0].conversationId;
                setMessageListUnreadCount(list[0].unreadCount || 0);
            }
        }
    }, [effectiveConversations, selectConversationId]);

    React.useEffect(() => {
        //for use in ws
        selectConversationIdRef.current = selectConversationId;
        if (selectConversationId) {
            fetchMessageByConversationId(selectConversationId);
        }
    }, [selectConversationId]);

    React.useEffect(() => {
        if (clickSendMsgFlag) {
            handleShowMsgModal();
        }
    }, [clickSendMsgFlag]);

    return (
        <div className={`${s.wrap} ${isEmpty ? s.emptyWrap : ''}`}>
            <div className={s.left}>
                {showGroupPracticeView && (
                    <div className={s.scopeToggle}>
                        <button
                            type="button"
                            className={`${s.scopeToggleBtn} ${gpScope === 'my' ? s.scopeToggleBtnActive : ''}`}
                            onClick={() => handleScopeChange('my')}
                        >
                            My messages
                        </button>
                        <button
                            type="button"
                            className={`${s.scopeToggleBtn} ${gpScope === 'all' ? s.scopeToggleBtnActive : ''}`}
                            onClick={() => handleScopeChange('all')}
                        >
                            All patient messages
                        </button>
                    </div>
                )}
                {showGroupPracticeView && (
                    <div className={s.convSearch}>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                            placeholder="Search messages..."
                            value={gpSearchQuery}
                            onChange={(e) => setGPSearchQuery(e.target.value)}
                            allowClear
                            size="large"
                            className={s.convSearchInput}
                        />
                    </div>
                )}
                {!hideUnread && (
                    <div
                        onClick={() => {
                            if (!hasUnreadMessageAll && !showUnreadConversationOnly) {
                                return;
                            }
                            setShowUnreadConversationOnly(!showUnreadConversationOnly);
                        }}
                        className={`${s.unReadLabel} ${
                            hasUnreadMessageAll ? s.unReadLabelEnable : ''
                        } ${showUnreadConversationOnly ? s.unReadLabelSelect : ''}`}
                    >
                        Unread ({effectiveUnreadCount || 0})
                    </div>
                )}

                {showGroupPracticeView && gpScope === 'all' && gpSelectedProviderId && gpSelectedProvider && (
                    <div className={s.providerPickerArea}>
                        <div className={s.viewAsBanner}>
                            <div className={s.viewAsBannerAccent} />
                            <div className={s.viewAsBannerContent}>
                                <span>
                                    Viewing as{' '}
                                    <strong>
                                        {gpSelectedProvider.firstName} {gpSelectedProvider.lastName}
                                    </strong>
                                </span>
                                <button
                                    type="button"
                                    className={s.viewAsExit}
                                    onClick={() => {
                                        setGPSelectedProviderId(undefined);
                                        setAllMsgConversations(undefined);
                                        setSelectConversationId(undefined);
                                        selectConversationIdRef.current = undefined;
                                        setMessageList(undefined);
                                    }}
                                >
                                    Exit
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className={s.list}>
                    {displayConversation?.map((item) => {
                        const hasUnread = !!(item.unreadCount && item.unreadCount > 0);
                        const updateTime = dayjs(item.lastMessageAt);
                        const isToday = updateTime.isSame(dayjs(), 'day');
                        const isMyConversation = item.members?.some(
                            (member) => member.userId === getUserId() || member.userId === currentUser?.user?.id,
                        );
                        const isProviderReview =
                            item.conversationType === EConversationType.PROVIDER_REVIEW ||
                            item.conversationType === 'PROVIDER_REVIEW';
                        const itemInfo = getInfoFromConversation(item);

                        const gpParsed =
                            showGroupPracticeView || isInGroup ? parseGPConversationId(item.conversationId) : null;

                        let displayTitle = '';
                        let labelText = '';
                        let hasAvatar = false;
                        let avatarUrl = '';
                        let isOnline = false;
                        let roleBadge: React.ReactNode = null;
                        let providerTagText = '';
                        let avatarColor = AVATAR_COLORS[0];

                        if ((showGroupPracticeView || isInGroup) && gpParsed && gpParsed.type !== 'NORMAL') {
                            const myUserId = currentUser?.user?.id;
                            const myActingUserId = currentUser?.actingUser?.id;
                            const gpInfo = getGPConversationDisplayInfo(
                                gpParsed,
                                gpPatientResults,
                                teamMembers,
                                myUserId,
                                myActingUserId,
                            );
                            displayTitle = gpInfo.displayTitle;
                            labelText = gpInfo.labelText;
                            roleBadge = gpInfo.roleBadge;
                            avatarColor = gpInfo.avatarColor;
                            if (item.members) {
                                const providerMember = item.members.find(
                                    (m) => m.userType !== 'GP_PROSPECT_IDENTITY' && m.userType !== 'PATIENT',
                                );
                                if (providerMember) {
                                    providerTagText =
                                        `${providerMember.firstName || ''} ${providerMember.lastName || ''}`.trim();
                                }
                            }
                        } else {
                            displayTitle = isProviderReview
                                ? 'Klarity reviews'
                                : `${itemInfo.patientFirstName || ''} ${itemInfo.patientLastName || ''}`.trim();
                            hasAvatar = !!itemInfo.patientAvatarUrl && itemInfo.patientAvatarUrl !== '';
                            avatarUrl = itemInfo.patientAvatarUrl || '';
                            isOnline = itemInfo.patientOnline || false;
                            if (itemInfo.patientFirstName) labelText += itemInfo.patientFirstName.substring(0, 1);
                            if (itemInfo.patientLastName) labelText += itemInfo.patientLastName.substring(0, 1);
                            avatarColor = getAvatarColor(itemInfo.patientFirstName || '');
                            const hasPatientMember = item.members?.some(
                                (m) => m.userType === EMessageSenderType.PATIENT,
                            );
                            if (showGroupPracticeView && hasPatientMember) {
                                roleBadge = <span className={`${s.roleBadge} ${s.roleBadgePatient}`}>Patient</span>;
                                if (item.members) {
                                    const providerMember = item.members.find(
                                        (m) => m.userType !== 'PATIENT' && m.userType !== 'GP_PROSPECT_IDENTITY',
                                    );
                                    if (providerMember) {
                                        providerTagText =
                                            `${providerMember.firstName || ''} ${providerMember.lastName || ''}`.trim();
                                    }
                                }
                            }
                        }

                        let name = '';
                        if (item.lastMessageSenderType === EMessageSenderType.PROVIDER) {
                            name = 'You';
                        } else if (item.lastMessageSenderType === EMessageSenderType.SYSTEM) {
                            name = 'System';
                        } else if (item.lastMessageSenderType === EMessageSenderType.GP_USER) {
                            const member = item.members?.find((m) => m.userType === EMessageSenderType.GP_USER);
                            if (member?.userId === currentUser?.user?.id) {
                                name = 'You';
                            } else {
                                name = `${member?.firstName} ${member?.lastName}`.trim();
                            }
                        } else {
                            name = `${itemInfo.patientFirstName || ''} ${itemInfo.patientLastName || ''}`.trim();
                        }

                        const showProviderTag = !!(showGroupPracticeView && gpScope === 'all' && providerTagText);
                        // console.log({
                        //     showProviderTag,
                        //     providerTagText,
                        //     gpScope,
                        //     showGroupPracticeView,
                        // });
                        return (
                            <div
                                className={`${s.item} ${hasUnread ? s.itemUnread : ''} ${
                                    selectConversationId === item.conversationId ? s.itemSelect : ''
                                }`}
                                key={item.conversationId}
                                onClick={() => {
                                    setClearMessageFlag({});
                                    setResetFlag({});
                                    handleChangeConversation(item.conversationId);
                                }}
                            >
                                <div className={s.convAvatarWrap}>
                                    {hasAvatar ? (
                                        <img src={avatarUrl} className={s.convAvatar} />
                                    ) : (
                                        <div className={s.convAvatarInitials} style={{ background: avatarColor }}>
                                            {labelText}
                                        </div>
                                    )}
                                    {isOnline && <span className={s.convOnlineDot} />}
                                </div>
                                <div className={s.convBody}>
                                    <div className={s.convNameRow}>
                                        <span className={s.convName}>{displayTitle}</span>
                                        {roleBadge}
                                        {hasUnread && isMyConversation && (
                                            <span className={s.unReadCount}>{item.unreadCount}</span>
                                        )}
                                    </div>
                                    <div className={s.content}>
                                        {name}: {conversationPreview(item)}
                                    </div>

                                    <div className={s.timeRow}>
                                        <ClockCircleOutlined
                                            style={{
                                                color: '#9CA3AF',
                                                marginRight: 4,
                                                fontSize: 12,
                                            }}
                                        />
                                        <span className={s.time}>
                                            {isToday ? 'Today, ' : ''}{' '}
                                            {getTimeStr(dayjs(item.lastMessageAt), 'MMM DD, hh:mm A')}
                                        </span>
                                    </div>
                                    {showProviderTag && (
                                        <div className={s.convProviderTag}>With: {providerTagText}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {hasMoreConversation && (
                        <div
                            className={s.viewMore}
                            onClick={() => {
                                fetchConversationSize.current = 1000;
                            }}
                        >
                            <Button block>View more</Button>
                        </div>
                    )}
                </div>
            </div>
            <div className={s.mid}>
                {!isEmpty && effectiveConversations !== undefined && (
                    <>
                        <div
                            onClick={() => {
                                setShowClientInfo(!showClientInfo);
                            }}
                            className={s.cName}
                        >
                            <div className={s.chatHdrAvatar} style={{ background: conversationAvatarColor }}>
                                <span className={s.chatHdrInitials}>{conversationLabelText}</span>
                                {targetConversationInfo?.patientOnline && <span className={s.chatHdrOnlineDot} />}
                            </div>
                            <div className={s.chatHdrInfo}>
                                <div className={s.chatHdrNameRow}>
                                    <span className={s.chatHdrName}>{conversationDisplayTitle}</span>
                                    {conversationRoleBadge}
                                </div>
                            </div>
                        </div>

                        <div className={s.messageWrap} id="messageWrap">
                            {loadingHistoryMessageRef.current && <Spin className={s.messageLoadingHistory} />}
                            <InboxMessageList
                                clientId={patientId}
                                inboxMessageList={displayMessage}
                                inboxConversation={targetConversation}
                                onReviewReplySuccess={() => {
                                    // Review 回复成功后，重新拉取当前会话的消息列表和会话列表
                                    resetAutoFetchInterval();
                                    refetchConversation(fetchConversationSize.current, urlClientId.current);
                                    refetchAllUnreadCount();
                                    const currentCid = selectConversationIdRef.current || conversationId;
                                    if (currentCid) {
                                        fetchMessageByConversationId(currentCid);
                                    }
                                }}
                                billingPolicy={billingPolicy}
                                skipReadAction={gpScope === 'all'}
                                connectAccountInfo={connectAccountInfo}
                            />
                            {loadingConversationMessage && !loadingHistoryMessageRef.current && (
                                <Spin className={s.messageLoading} />
                            )}
                        </div>
                        {isPPAProvider && showBottomAction && (
                            <div className={s.reminderButtonsContainer}>
                                <IntakeFormReminderButton
                                    targetClient={targetClient}
                                    patientId={patientId}
                                    selectConversationId={selectConversationId}
                                    conversationMessageMap={conversationMessageMap}
                                    setMessageList={setMessageList}
                                    scrollToLatestMessage={scrollToLatestMessage}
                                    resetAutoFetchInterval={resetAutoFetchInterval}
                                    refetchConversation={refetchConversation}
                                    refetchAllUnreadCount={refetchAllUnreadCount}
                                    fetchConversationSize={fetchConversationSize}
                                    urlClientId={urlClientId}
                                    run={run}
                                />
                                <AppointmentReminderButton
                                    targetClient={targetClient}
                                    patientId={patientId}
                                    selectConversationId={selectConversationId}
                                    conversationMessageMap={conversationMessageMap}
                                    setMessageList={setMessageList}
                                    scrollToLatestMessage={scrollToLatestMessage}
                                    resetAutoFetchInterval={resetAutoFetchInterval}
                                    refetchConversation={refetchConversation}
                                    refetchAllUnreadCount={refetchAllUnreadCount}
                                    fetchConversationSize={fetchConversationSize}
                                    urlClientId={urlClientId}
                                    run={run}
                                    isPracticeView={!!showGroupPracticeView}
                                />
                            </div>
                        )}

                        {!isTargetProviderReview &&
                            showInput &&
                            !disabledSendToPatient &&
                            !disabledSendAndAddHolder && (
                                <div className={s.messageInput}>
                                    {/* <div className={s.aiWrap}>
                                    <AiTextEditor
                                        rows={6}
                                        onChange={(e) => {
                                            if (typeof e === 'string') {
                                                setReplayContent(e as string);
                                            } else {
                                                setReplayContent(
                                                    e ? e.target.value : '',
                                                );
                                            }
                                        }}
                                        clearMessageFlag={clearMessageFlag}
                                        value={replayContent}
                                        type={EAIEditorType.PROSPECT}
                                        resetFlag={resetFlag}
                                        maxLength={3000}
                                        narrow={inputWidth < 750}
                                    />
                                </div>

                                <div className={s.btnWrap}>
                                    <Button
                                        className={s.btn}
                                        loading={replyingMsg}
                                        onClick={() => {
                                            onClickReply();
                                        }}
                                        type="primary"
                                        disabled={!replayContent}
                                    >
                                        Send
                                    </Button>
                                </div> */}
                                    <ChatInput
                                        showNewTip
                                        value={replayContentValue}
                                        onChange={(value) => {
                                            console.log(value);
                                            setReplayContentValue(value);
                                        }}
                                        onSend={onClickReply}
                                        placeholder={
                                            disabledSendAndAddHolder
                                                ? 'You can view this conversation, but only the patient’s assigned provider can send messages.'
                                                : 'Enter your message'
                                        }
                                        disabled={replyingMsg || disabledSendAndAddHolder}
                                        type={EAIEditorType.PROSPECT}
                                        clientId={targetClientDetail?.id || patientId || 0}
                                    />
                                </div>
                            )}
                    </>
                )}
                {isEmpty && effectiveConversations !== undefined && (
                    <div className={s.emptyWrap}>
                        <img src={EmptyIcon} />
                        <div className={s.emptyTitle}>No messages yet</div>
                        <div className={s.emptyDes}>
                            Appointment updates, system notifications, and client messages will show up here. Start a
                            conversation with your clients anytime!
                        </div>
                        <Tooltip
                            title={
                                disabledSendToPatient
                                    ? 'You are viewing other providers’ messages. Please switch to your own view to send messages.'
                                    : ''
                            }
                        >
                            <Button onClick={handleShowMsgModal} type="primary" disabled={disabledSendToPatient}>
                                Send message
                            </Button>
                        </Tooltip>
                    </div>
                )}
            </div>
            {showClientInfo && targetClient && !isEmpty && (
                <div className={s.right}>
                    <div className={s.pTitle}>
                        <span>Client details</span>
                        <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                setShowClientInfo(false);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="25"
                                viewBox="0 0 24 25"
                                fill="none"
                            >
                                <path
                                    d="M6 18.5L18 6.5M6 6.5L18 18.5"
                                    stroke="#111827"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                    <div className={s.pWrap}>
                        <ProsepectInfo
                            billingPolicy={billingPolicy}
                            connectAccountInfo={connectAccountInfo}
                            forceResetInfo={forceResetInfo}
                            useForModule
                            smallSize
                            data={loadingTargetClientDetail ? undefined : targetClientDetail}
                            onUpdate={() => {
                                run();
                            }}
                        />
                    </div>
                </div>
            )}
            <div className={`${s.newMsgWrap} ${showNewMsgModal ? s.newMsgWrapShow : ''}`}>
                <div className={s.nTitle}>
                    <span>New message</span>
                    <svg
                        onClick={() => {
                            setNewMsgClientId(undefined);
                            setReplayContentValue({ text: '', files: [] });
                            setResetFlag({});
                            setShowNewMsgModal(false);
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M6 18L18 6M6 6L18 18"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
                <div className={s.nContent}>
                    {showTeamTab && (
                        <>
                            <div className={s.nLabel}>Send to</div>
                            <div className={s.recipientToggle}>
                                {showPatientTab && (
                                    <Button
                                        className={`${s.recipientToggleBtn} ${sendToType === 'patient' ? s.recipientToggleBtnActive : ''}`}
                                        onClick={() => {
                                            setSendToType('patient');
                                            setNewMsgClientId(undefined);
                                        }}
                                    >
                                        Patient
                                    </Button>
                                )}
                                <Button
                                    className={`${s.recipientToggleBtn} ${sendToType === 'team' ? s.recipientToggleBtnActive : ''}`}
                                    onClick={() => {
                                        setSendToType('team');
                                        setNewMsgClientId(undefined);
                                    }}
                                >
                                    Team member
                                </Button>
                            </div>
                        </>
                    )}
                    <div className={s.nLabel}>Recipient</div>
                    {sendToType === 'patient' && useGPPatientSearch && (
                        <Select
                            showSearch
                            optionLabelProp="label"
                            className={s.select}
                            onChange={(val) => {
                                setNewMsgClientId(val);
                            }}
                            filterOption={(input, option) => {
                                const name = String(option?.['data-name'] || '');
                                return name.toLowerCase().includes(input.toLowerCase());
                            }}
                            value={newMsgClientId}
                            placeholder="Search patient..."
                            style={{ width: '200px' }}
                        >
                            {gpPatientResults.map((item) => {
                                const fullName = `${item.firstName} ${item.lastName}`;
                                return (
                                    <Select.Option
                                        value={item.identityId}
                                        key={item.identityId}
                                        label={fullName}
                                        data-name={`${fullName} ${item.providerName || ''}`}
                                    >
                                        {fullName}
                                        {item.providerName && (
                                            <span style={{ color: '#9CA3AF', marginLeft: 4 }}>
                                                ({item.providerName})
                                            </span>
                                        )}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    )}
                    {sendToType === 'patient' && showPatientTab && !useGPPatientSearch && (
                        <Select
                            showSearch
                            className={s.select}
                            onChange={(val) => {
                                setNewMsgClientId(val);
                            }}
                            filterOption={(input, option) => {
                                const name = option?.children?.join('');
                                if (!name) return true;
                                return name?.toLowerCase().indexOf(input?.toLowerCase()) >= 0;
                            }}
                            value={newMsgClientId}
                            placeholder="Select recipient..."
                            style={{ width: '200px' }}
                        >
                            {clientOption?.map((item: Prospect) => {
                                return (
                                    <Select.Option value={item.id} key={item.id}>
                                        {item.firstName} {item.lastName}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    )}
                    {sendToType === 'team' && showTeamTab && (
                        <Select
                            showSearch
                            className={s.select}
                            optionLabelProp="label"
                            onChange={(val) => {
                                setNewMsgClientId(val);
                            }}
                            filterOption={(input, option) => {
                                const name = option?.['data-name'] as string;
                                if (!name) return true;
                                return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            }}
                            value={newMsgClientId}
                            placeholder="Select recipient..."
                            style={{ width: '200px' }}
                        >
                            {gpTeamMemberResults
                                .filter((member) => member.userId !== currentUser?.user?.id)
                                .map((member) => {
                                    const fullName = `${member.firstName} ${member.lastName}`;
                                    const initials =
                                        `${(member.firstName || '')[0] || ''}${(member.lastName || '')[0] || ''}`.toUpperCase();
                                    let roleName = '';
                                    let roleClass = '';
                                    let roleSub = '';
                                    let avatarColor = '#0E62F4';
                                    switch (member.roleCode) {
                                        case ERoleCode.GROUP_OWNER:
                                            roleName = 'Owner';
                                            roleClass = 'owner';
                                            roleSub = 'Practice owner';
                                            break;
                                        case ERoleCode.GROUP_PROVIDER:
                                            roleName = 'Provider';
                                            roleClass = 'provider';
                                            roleSub = 'Provider';
                                            break;
                                        case ERoleCode.GROUP_PRACTICE_MANAGER:
                                            roleName = 'Practice manager';
                                            roleClass = 'practice-mgr';
                                            roleSub = 'Operations & team workflows';
                                            avatarColor = '#92400E';
                                            break;
                                        case ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT:
                                            roleName = 'Admin assistant';
                                            roleClass = 'admin-asst';
                                            roleSub = 'Scheduling & patient communication';
                                            avatarColor = '#1E40AF';
                                            break;
                                        case ERoleCode.GROUP_CLINICAL_ASSISTANT:
                                            roleName = 'Clinical assistant';
                                            roleClass = 'clinical-asst';
                                            roleSub = 'Labs & prescriptions support';
                                            avatarColor = '#065F46';
                                            break;
                                        default:
                                            roleName = 'Staff';
                                            roleClass = 'staff';
                                            roleSub = '';
                                            break;
                                    }
                                    const roleTagStyles: Record<string, React.CSSProperties> = {
                                        owner: { background: '#0E62F4', color: '#FFFFFF' },
                                        provider: { background: '#EDE9FE', color: '#5B21B6' },
                                        'practice-mgr': { background: '#FEF3C7', color: '#92400E' },
                                        'admin-asst': { background: '#DBEAFE', color: '#1E40AF' },
                                        'clinical-asst': { background: '#D1FAE5', color: '#065F46' },
                                        staff: { background: '#F3F4F6', color: '#4B5563' },
                                    };
                                    return (
                                        <Select.Option
                                            value={member.userId}
                                            key={member.userId}
                                            label={fullName}
                                            data-name={fullName}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: '4px 0',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: '#fff',
                                                        background: avatarColor,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {initials}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div
                                                        style={{
                                                            fontSize: 13,
                                                            fontWeight: 500,
                                                            color: '#111827',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 6,
                                                        }}
                                                    >
                                                        {fullName}
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                padding: '1px 6px',
                                                                borderRadius: 4,
                                                                fontSize: 10,
                                                                fontWeight: 500,
                                                                whiteSpace: 'nowrap',
                                                                ...roleTagStyles[roleClass],
                                                            }}
                                                        >
                                                            {roleName}
                                                        </span>
                                                    </div>
                                                    {roleSub && (
                                                        <div style={{ fontSize: 11, color: '#6B7280' }}>{roleSub}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </Select.Option>
                                    );
                                })}
                        </Select>
                    )}
                    <div className={s.nLabel}>Message</div>
                    <div className={s.inputWrap}>
                        {/* <AiTextEditor
                            size="small"
                            rows={6}
                            maxLength={3000}
                            onChange={(e) => {
                                if (typeof e === 'string') {
                                    setNewMsgContent(e as string);
                                } else {
                                    setNewMsgContent(e ? e.target.value : '');
                                }
                            }}
                            value={newMsgContent}
                            type={EAIEditorType.PROSPECT}
                            resetFlag={resetFlag}
                            placeholder=""
                        /> */}
                        <ChatInput
                            value={newMsgContent}
                            onChange={(value) => {
                                setNewMsgContent(value);
                            }}
                            type={EAIEditorType.PROSPECT}
                            clientId={newMsgClientId!}
                            hideSend
                        />
                    </div>
                </div>
                <div className={s.nFooter}>
                    <Button style={{ marginRight: '16px' }} onClick={onClickDiscard}>
                        Discard
                    </Button>
                    <Button
                        loading={sendingNewMsg}
                        disabled={!newMsgClientId || (!newMsgContent.text.trim() && newMsgContent.files.length === 0)}
                        type="primary"
                        onClick={onClickSend}
                    >
                        Send
                    </Button>
                </div>
            </div>
            <TextVisitUnSubmittedDrawer
                show={showTextVisitUnSubmittedDrawer}
                onClose={() => setShowTextVisitUnSubmittedDrawer(false)}
                onNext={(data) => {
                    const { nextTextVisitUuid, inProgress } = data;
                    // console.log('onNext', nextTextVisitUuid, inProgress);
                    if (nextTextVisitUuid) {
                        setSelectedCase(undefined);
                        setNextCaseUuid(nextTextVisitUuid);
                        if (inProgress) {
                            setShowTextVisitUnSubmittedDrawer(true);
                        } else {
                            setShowPreviewDrawer(true);
                        }
                    }
                }}
                onSave={() => {}}
                textVisitUuid={selectedCase?.textVisitUuid || nextCaseUuid}
            />
        </div>
    );
};

export default MessageContainer;
